import { LiveViewEvent } from "@/LiveViewPage/liveViewConnection";
import { InstallSdkData } from "./types";

const WEB_LIBRARY = "web";

export function eventLabel(event: LiveViewEvent): string {
  return event.event_name || event.type || "Unnamed event";
}

function parseHost(host: string): { hostname: string; port: string } | null {
  try {
    const url = new URL(`http://${host}`);
    return { hostname: url.hostname.replace(/^www\./, ""), port: url.port };
  } catch {
    return null;
  }
}

/**
 * Whether an event's host belongs to the site the user entered. `www.` is
 * ignored, and subdomains of the entered site count. A port only has to
 * match when the user entered one.
 */
export function hostMatchesSite(eventHost: string, siteHost: string): boolean {
  const event = parseHost(eventHost);
  const site = parseHost(siteHost);
  if (!event || !site) {
    return false;
  }
  if (site.port && event.port !== site.port) {
    return false;
  }
  return (
    event.hostname === site.hostname ||
    event.hostname.endsWith(`.${site.hostname}`)
  );
}

function isFromSite(host: string | undefined, siteUrl: string | null) {
  if (!siteUrl) {
    return true;
  }
  return !!host && hostMatchesSite(host, new URL(siteUrl).host);
}

/**
 * Whether a Live View event shows the web snippet is installed: it must come
 * from the JavaScript SDK, and from the entered site when there is one.
 */
export function isSiteInstallEvent(
  event: LiveViewEvent,
  siteUrl: string | null,
): boolean {
  return (
    event.library === WEB_LIBRARY &&
    isFromSite(event.built_in_properties?.host, siteUrl)
  );
}

/**
 * The recorded first event, or null once the user has entered a site it
 * didn't come from.
 */
export function firstEventForSite(
  firstEvent: InstallSdkData["firstEvent"],
  siteUrl: string | null,
): InstallSdkData["firstEvent"] {
  return firstEvent && isFromSite(firstEvent.host, siteUrl) ? firstEvent : null;
}

export function toFirstEvent(
  event: LiveViewEvent,
): NonNullable<InstallSdkData["firstEvent"]> {
  return {
    name: eventLabel(event),
    host: event.built_in_properties?.host,
    path: event.path,
  };
}

/**
 * The first event to record once the user enters a site: the recorded one if
 * it came from that site, otherwise the oldest received event that did.
 * `received` is newest first.
 */
export function firstEventAfterSiteChange(
  firstEvent: InstallSdkData["firstEvent"],
  received: LiveViewEvent[],
  siteUrl: string | null,
): InstallSdkData["firstEvent"] {
  if (firstEventForSite(firstEvent, siteUrl)) {
    return firstEvent;
  }
  const oldest = received.findLast((event) =>
    isSiteInstallEvent(event, siteUrl),
  );
  return oldest ? toFirstEvent(oldest) : firstEvent;
}
